/**
 * D1b Task 7: regulators and regulatory circulars.
 * Backend: app/django-core/apps/regchange/ (views.py, urls.py); contract in the Task 6
 * report's URL table (prefix /api/v1/regchange/).
 *
 * - Company endpoints (my-regulators, sectors, circulars) take the tenant from the
 *   session; the client never sends a tenant.
 * - Staff endpoints (regulators, watch-pages, alerts, platform-circulars) are
 *   IsAdminUser (is_staff).
 * - Lists are paginated ({count, next, previous, results}). my-regulators, regulators
 *   and watch-pages feed dropdowns, so they ask for page_size=500 (the API maximum).
 * - Documents are streamed by the scoped document action as an attachment and saved
 *   from a blob; no storage path or URL is ever used.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';

const BASE = '/api/v1/regchange';
const ALL = { page_size: 500 };

/**
 * One path segment from an id. Ids can come from the address bar, so "../" or "/" in
 * one must never reach another endpoint with the user's credentials.
 */
const seg = (id) => encodeURIComponent(String(id));

/** Drop empty filter values so the API never sees `?regulator=`. */
const compact = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  );

/** Filename from Content-Disposition; a malformed encoded name falls back to its raw text. */
export const filenameFromDisposition = (header) => {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || null;
};

/** Blob on success; the JSON error body otherwise, or {} for an error page that is not JSON. */
export async function blobOrJson(response) {
  if (response.ok) return response.blob();
  try {
    return await response.json();
  } catch {
    return {};
  }
}

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

/** Multipart body for circulars/upload/: a file OR text, plus the filled fields. */
export function uploadBody({ regulator, title, reference, published_on: publishedOn, file, text }) {
  const body = new FormData();
  body.append('regulator', regulator);
  body.append('title', title);
  if (reference) body.append('reference', reference);
  if (publishedOn) body.append('published_on', publishedOn);
  if (file) body.append('file', file);
  else if (text !== undefined) body.append('text', text);
  return body;
}

export const regchangeApi = createApi({
  reducerPath: 'regchangeApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'MyRegulator',
    'Sectors',
    'Circular',
    'Regulator',
    'WatchPage',
    'Alert',
    'Analysis',
    'Remediation',
  ],
  endpoints: (builder) => ({
    // ---- Company ----
    getMyRegulators: builder.query({
      query: () => ({ url: `${BASE}/my-regulators/`, params: ALL }),
      providesTags: ['MyRegulator'],
    }),
    getSectors: builder.query({
      query: () => ({ url: `${BASE}/sectors/` }),
      providesTags: ['Sectors'],
    }),
    updateSectors: builder.mutation({
      query: (extraSectors) => ({
        url: `${BASE}/sectors/`,
        method: 'PUT',
        body: { extra_sectors: extraSectors },
      }),
      // Extra sectors change which regulators, and so which circulars, apply.
      invalidatesTags: ['Sectors', 'MyRegulator', 'Circular'],
    }),
    getCirculars: builder.query({
      query: (params = {}) => ({ url: `${BASE}/circulars/`, params: compact(params) }),
      providesTags: ['Circular'],
    }),
    getCircular: builder.query({
      query: (id) => ({ url: `${BASE}/circulars/${seg(id)}/` }),
      providesTags: (result, error, id) => [{ type: 'Circular', id }],
    }),
    uploadCircular: builder.mutation({
      query: (fields) => ({
        url: `${BASE}/circulars/upload/`,
        method: 'POST',
        body: uploadBody(fields),
      }),
      invalidatesTags: ['Circular'],
    }),
    reExtractCircular: builder.mutation({
      query: (id) => ({
        url: `${BASE}/circulars/${seg(id)}/re-extract/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Circular', id }, 'Circular'],
    }),
    downloadCircularDocument: builder.mutation({
      async queryFn({ id, scope = 'company', filename }, _api, _extra, baseQuery) {
        const prefix = scope === 'platform' ? 'platform-circulars' : 'circulars';
        const result = await baseQuery({
          url: `${BASE}/${prefix}/${seg(id)}/document/`,
          responseHandler: blobOrJson,
        });
        if (result.error) return { error: result.error };
        const name =
          filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
          filename ||
          'circular';
        saveBlob(result.data, name);
        return { data: { filename: name } };
      },
    }),

    // ---- Company analysis (D1c) ----
    // Every decision changes the badge, the inbox rows and the analysis, so all of them
    // share the 'Analysis' tag; remediation writes also refresh 'Remediation'.
    getInbox: builder.query({
      query: (params = {}) => ({ url: `${BASE}/inbox/`, params: compact(params) }),
      providesTags: ['Analysis'],
    }),
    getInboxCount: builder.query({
      query: () => ({ url: `${BASE}/inbox/count/` }),
      providesTags: ['Analysis'],
    }),
    analyseCircular: builder.mutation({
      query: (id) => ({ url: `${BASE}/circulars/${seg(id)}/analyse/`, method: 'POST', body: {} }),
      // The circular carries the company's analysis_id and Analyse state, so it refetches.
      invalidatesTags: (result, error, id) => ['Analysis', { type: 'Circular', id }],
    }),
    getAnalysis: builder.query({
      query: (id) => ({ url: `${BASE}/analyses/${seg(id)}/` }),
      providesTags: ['Analysis'],
    }),
    markNotRelevant: builder.mutation({
      query: ({ id, reason }) => ({
        url: `${BASE}/analyses/${seg(id)}/not-relevant/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Analysis'],
    }),
    acceptAnalysisItem: builder.mutation({
      query: ({ id, edited }) => ({
        url: `${BASE}/analysis-items/${seg(id)}/accept/`,
        method: 'POST',
        body: edited ? { edited } : {},
      }),
      invalidatesTags: ['Analysis', 'Remediation'],
    }),
    rejectAnalysisItem: builder.mutation({
      query: ({ id, reason }) => ({
        url: `${BASE}/analysis-items/${seg(id)}/reject/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Analysis'],
    }),
    // A suggested first due date and frequency read from the item's deadline wording
    // ({due_date, frequency, recurring, source: "parsed"|"ai"|null, quote}). Never saved.
    suggestDates: builder.mutation({
      query: (id) => ({
        url: `${BASE}/analysis-items/${seg(id)}/suggest-dates/`,
        method: 'POST',
        body: {},
      }),
    }),
    getRemediation: builder.query({
      query: (params = {}) => ({ url: `${BASE}/remediation/`, params: compact(params) }),
      providesTags: ['Remediation'],
    }),
    updateRemediation: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/remediation/${seg(id)}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Remediation', 'Analysis'],
    }),
    convertRemediation: builder.mutation({
      // library_ref: the master-data library entry the new record is filed under.
      query: ({ id, to, library_ref }) => ({
        url: `${BASE}/remediation/${seg(id)}/convert/`,
        method: 'POST',
        body: library_ref ? { to, library_ref } : { to },
      }),
      invalidatesTags: ['Remediation', 'Analysis'],
    }),

    // ---- Platform staff ----
    getRegulators: builder.query({
      query: () => ({ url: `${BASE}/regulators/`, params: ALL }),
      providesTags: ['Regulator'],
    }),
    createRegulator: builder.mutation({
      query: (body) => ({ url: `${BASE}/regulators/`, method: 'POST', body }),
      invalidatesTags: ['Regulator', 'MyRegulator'],
    }),
    updateRegulator: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/regulators/${seg(id)}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Regulator', 'MyRegulator', 'Alert'],
    }),
    deleteRegulator: builder.mutation({
      query: (id) => ({ url: `${BASE}/regulators/${seg(id)}/`, method: 'DELETE' }),
      invalidatesTags: ['Regulator', 'MyRegulator', 'WatchPage'],
    }),
    getWatchPages: builder.query({
      query: () => ({ url: `${BASE}/watch-pages/`, params: ALL }),
      providesTags: ['WatchPage'],
    }),
    createWatchPage: builder.mutation({
      query: (body) => ({ url: `${BASE}/watch-pages/`, method: 'POST', body }),
      invalidatesTags: ['WatchPage'],
    }),
    updateWatchPage: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/watch-pages/${seg(id)}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['WatchPage'],
    }),
    deleteWatchPage: builder.mutation({
      query: (id) => ({ url: `${BASE}/watch-pages/${seg(id)}/`, method: 'DELETE' }),
      invalidatesTags: ['WatchPage'],
    }),
    checkWatchPage: builder.mutation({
      query: (id) => ({ url: `${BASE}/watch-pages/${seg(id)}/check/`, method: 'POST', body: {} }),
      invalidatesTags: ['WatchPage', 'Alert'],
    }),
    testLinkPattern: builder.mutation({
      // Matched by the API with RE2 under the fetcher's href rules; nothing is fetched.
      query: (body) => ({ url: `${BASE}/watch-pages/test-pattern/`, method: 'POST', body }),
    }),
    getSectorOptions: builder.query({
      query: () => ({ url: `${BASE}/sector-options/` }),
    }),
    getAlerts: builder.query({
      query: (params = {}) => ({ url: `${BASE}/alerts/`, params: compact(params) }),
      providesTags: ['Alert'],
    }),
    acknowledgeAlert: builder.mutation({
      query: (id) => ({ url: `${BASE}/alerts/${seg(id)}/acknowledge/`, method: 'POST', body: {} }),
      invalidatesTags: ['Alert'],
    }),
    getPlatformCirculars: builder.query({
      query: (params = {}) => ({ url: `${BASE}/platform-circulars/`, params: compact(params) }),
      providesTags: ['Circular'],
    }),
    getPlatformCircular: builder.query({
      query: (id) => ({ url: `${BASE}/platform-circulars/${seg(id)}/` }),
      providesTags: (result, error, id) => [{ type: 'Circular', id }],
    }),
  }),
});

export const {
  useGetMyRegulatorsQuery,
  useGetSectorsQuery,
  useUpdateSectorsMutation,
  useGetCircularsQuery,
  useGetCircularQuery,
  useUploadCircularMutation,
  useReExtractCircularMutation,
  useDownloadCircularDocumentMutation,
  useGetInboxQuery,
  useGetInboxCountQuery,
  useAnalyseCircularMutation,
  useGetAnalysisQuery,
  useMarkNotRelevantMutation,
  useAcceptAnalysisItemMutation,
  useRejectAnalysisItemMutation,
  useSuggestDatesMutation,
  useGetRemediationQuery,
  useUpdateRemediationMutation,
  useConvertRemediationMutation,
  useGetRegulatorsQuery,
  useCreateRegulatorMutation,
  useUpdateRegulatorMutation,
  useDeleteRegulatorMutation,
  useGetWatchPagesQuery,
  useCreateWatchPageMutation,
  useUpdateWatchPageMutation,
  useDeleteWatchPageMutation,
  useCheckWatchPageMutation,
  useTestLinkPatternMutation,
  useGetSectorOptionsQuery,
  useGetAlertsQuery,
  useAcknowledgeAlertMutation,
  useGetPlatformCircularsQuery,
  useGetPlatformCircularQuery,
} = regchangeApi;
